import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import type { ReactNode } from 'react'

import { Layout, Seo } from '@/components/generals'
import { BlogCard } from '@/components/landing/blog-card'
import { ShowcaseCard } from '@/components/landing/showcase-card'
import { ShowcaseSlider } from '@/components/landing/showcase-slider'
import { TeamMember } from '@/components/landing/team-member'
import { getBlogPosts, getShowcases, getTeamMembers } from '@/lib/cms'

interface ServiceCard {
    id: string
    href: string
    icon: string
    iconWhite: string
    title: string
    description: string
}

const SERVICES: ServiceCard[] = [
    {
        id: 'mdl-node-_47e4f422-4993-2cf7-41bb-6c2304c92138-52a3927f',
        href: '/what-we-do#annual-coaching',
        icon: '/images/sync-content-refresh-sharie-filder-synchronize-2-1.svg',
        iconWhite: '/images/sync-white.svg',
        title: 'Annual Coaching',
        description: 'The Czechmate Year: one-on-one coaching with Dr. Brad Chvatal. The deep dive, the full game board, limited to 10 clients at a time.'
    },
    {
        id: 'mdl-node-_47e4f422-4993-2cf7-41bb-6c2304c9213a-52a3927f',
        href: '/what-we-do#power-session',
        icon: '/images/server.svg',
        iconWhite: '/images/server-white.svg',
        title: '90-Minute Power Session',
        description: 'Fast, focused and full of wins. We diagnose what is keeping you stuck and build a game plan you can use right away.'
    },
    {
        id: 'mdl-node-_47e4f422-4993-2cf7-41bb-6c2304c9213c-52a3927f',
        href: '/what-we-do#merchant-rates',
        icon: '/images/chart-infographic-icon.svg',
        iconWhite: '/images/chart-infographic-icon-white.svg',
        title: 'Merchant Services Rate Negotiation',
        description: 'Lower credit card processing rates for a one-time flat fee, plus the skills to monitor them and keep them low yourself.'
    },
    {
        id: 'mdl-node-_47e4f422-4993-2cf7-41bb-6c2304c9213e-52a3927f',
        href: '/what-we-do#treatment-coordinator',
        icon: '/images/connection.svg',
        iconWhite: '/images/connection-white.svg',
        title: 'Treatment Coordinator Training',
        description: 'Build confidence and close more cases. Six hours of one-on-one coaching across every phase of the patient journey.'
    },
    {
        id: 'mdl-node-_47e4f422-4993-2cf7-41bb-6c2304c92140-52a3927f',
        href: '/what-we-do#financial-coordinator',
        icon: '/images/chart-icon.svg',
        iconWhite: '/images/chart-icon-white.svg',
        title: 'Financial Coordinator Training',
        description: 'Clean up the chaos and lock in the cash. Four hours of training built to bring past-due balances under 1%.'
    },
    {
        id: 'mdl-node-_47e4f422-4993-2cf7-41bb-6c2304c92142-52a3927f',
        href: '/what-we-do#resin-to-retainer',
        icon: '/images/network-cloud.svg',
        iconWhite: '/images/network-cloud-white.svg',
        title: 'Resin to Retainer: A 3D Printing Masterclass',
        description: 'A self-paced online course on printing models and retainers in your own lab, taught by our lead lab tech, Bridget Scott.'
    }
]

const HomePage = ({ posts, items, team = [] }: InferGetStaticPropsType<typeof getStaticProps>): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Orthodontist Coaching in Eugene, OR'
                description='Orthodontic practice coaching from Dr. Brad Chvatal in Eugene, Oregon. Real strategy to grow your practice and build a life you actually want.'
            />

            <>
                <div className='hero-section home-hero-section' id='hero'>
                    <div className='container with-large-bottom-padding'>
                        <div className='hero-content'>
                            <div className='home-hero-content'>
                                <h1 className='hero-text-content home-hero-text-content no-margins'>
                                    <div className='hero-text-word'>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _1'>Providing</div>
                                        </div>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _2' style={{ color: 'var(--primary)' }}>Winning</div>
                                        </div>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _3'>Strategies</div>
                                        </div>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _4'>for</div>
                                        </div>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _5'>Your</div>
                                        </div>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _6'>Practice</div>
                                        </div>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _7'>&amp;</div>
                                        </div>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _9'>Your</div>
                                        </div>
                                        <div className='hero-text-letter'>
                                            <div className='hero-text _10'>Life</div>
                                        </div>
                                    </div>
                                </h1>
                            </div>
                        </div>
                    </div>
                    <div className='section-background'>
                        <img
                            className='home-section-bg-image'
                            src='/images/czechmate/hero-3d-scan.jpg'
                            sizes='100vw'
                            alt='Two clinicians reviewing a 3D scan of a dental arch on a monitor'
                            style={{ objectPosition: '0% 50%' }}
                        />
                        <div className='home-image-overlay' />
                    </div>
                </div>

                <div className='section slider-second-section animation-bottom-on-page-load' id='client-stories'>
                    <div className='container no-padding-top'>
                        <ShowcaseSlider items={items} />
                    </div>
                </div>

                <div className='section' id='coaching'>
                    <div className='container'>
                        <div className='mdl-layout-grid grid-2-col'>
                            <div className='content' id='mdl-node-_47e4f422-4993-2cf7-41bb-6c2304c92128-52a3927f'>
                                <div className='fade-in-move-on-scroll'>
                                    <h2 className='no-margins'>Meet Dr. Brad Chvatal, Your Czech Mate</h2>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-_47e4f422-4993-2cf7-41bb-6c2304c9212c-52a3927f'>
                                <div className='fade-in-move-on-scroll'>
                                    <div>
                                        After 23 years running his own orthodontic practice, scaling it to three locations
                                        and millions in production, Brad learned that success isn&apos;t just about the
                                        numbers. It&apos;s about building a life you actually want, with a practice that
                                        fuels it instead of draining it. No fluff. No cookie-cutter plans. Just real talk,
                                        real strategy and real results.
                                    </div>
                                </div>
                                <div className='fade-in-move-on-scroll'>
                                    <div className='margin-top-2em'>
                                        <div className='flex-horizontal'>
                                            <a className='button button-link mdl-inline-block' href='/what-we-do'>
                                                <div>Explore coaching</div>
                                                <span className='button-icon' aria-hidden='true' />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='mdl-layout-grid services-grid'>
                            {SERVICES.map((service) => (
                                <div className='content' id={service.id} key={service.id}>
                                    <a className='service-item-card-link mdl-inline-block' href={service.href}>
                                        <div className='service-item-card-bg' />
                                        <div className='service-item-content'>
                                            <div className='service-item-icon-holder'>
                                                <img className='service-card-item-image' src={service.icon} alt='' />
                                                <img className='service-card-item-white-image' src={service.iconWhite} alt='' />
                                            </div>
                                            <div className='service-item-title'>{service.title}</div>
                                            <div className='service-item-description-content'>
                                                <div>{service.description}</div>
                                                <div className='service-item-link'>
                                                    <div>Learn More</div>
                                                </div>
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className='section' id='stories'>
                    <div className='container'>
                        <div className='fade-in-move-on-scroll'>
                            <div className='section-info-center'>
                                <h2>Real Practices. Real Wins.</h2>
                                <p>
                                    Orthodontists who have worked with Brad and the Czechmate team, in their own words.
                                </p>
                            </div>
                        </div>
                        <div className='showcase-wrapper mdl-dyn-list'>
                            <div className='showcase-grid-items mdl-dyn-items' role='list'>
                                {items.slice(0, 3).map((item) => (
                                    <ShowcaseCard key={item.slug} item={item} />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className='section-background' />
                </div>

                <div className='section overflow-hidden' id='team'>
                    <div className='container no-padding-bottom'>
                        <div className='mdl-layout-grid grid-2-col'>
                            <div className='content' id='mdl-node-cd5cf431-ef62-0f41-a4dd-57843983330b-52a3927f'>
                                <div className='fade-in-move-on-scroll'>
                                    <h2 className='no-margins'>The Team Behind Czechmate</h2>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-cd5cf431-ef62-0f41-a4dd-57843983330f-52a3927f'>
                                <div className='fade-in-move-on-scroll'>
                                    <div>
                                        The same trainers who helped Brad build and scale his own practice now coach your
                                        team on case acceptance, collections, clinical operations and scheduling.
                                    </div>
                                </div>
                                <div className='fade-in-move-on-scroll'>
                                    <div className='margin-top-2em'>
                                        <div className='flex-horizontal'>
                                            <a className='button button-link mdl-inline-block' href='/who-we-are'>
                                                <div>Meet the team</div>
                                                <span className='button-icon' aria-hidden='true' />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='mdl-layout-grid team-members-grid fade-in-on-scroll'>
                            {team.map((member) => (
                                <div className='content' key={member.id}>
                                    <TeamMember member={member} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className='section overflow-hidden' id='articles'>
                    <div className='container'>
                        <div className='mdl-layout-grid grid-2-col'>
                            <div className='content' id='mdl-node-ebf46ca1-f868-8329-1517-abd12afed64b-52a3927f'>
                                <div className='fade-in-move-on-scroll'>
                                    <h2 className='no-margins'>Articles for Orthodontic Practices</h2>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-ebf46ca1-f868-8329-1517-abd12afed64f-52a3927f'>
                                <div className='fade-in-move-on-scroll'>
                                    <div>
                                        Practical reading on patient experience, treatment coordination and growing a
                                        practice that supports the life you want.
                                    </div>
                                </div>
                                <div className='fade-in-move-on-scroll'>
                                    <div className='margin-top-2em'>
                                        <div className='flex-horizontal'>
                                            <a className='button button-link mdl-inline-block' href='/insights'>
                                                <div>View All</div>
                                                <span className='button-icon' aria-hidden='true' />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='blog-wrapper mdl-dyn-list'>
                            <div className='blog-grid mdl-dyn-items' role='list'>
                                {posts.slice(0, 3).map((post) => (
                                    <BlogCard key={post.slug} post={post} />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className='section-background' />
                </div>
            </>
        </Layout>
    )
}

export const getStaticProps = (async () => {
    const [posts, items, team] = await Promise.all([getBlogPosts(), getShowcases(), getTeamMembers()])
    return { props: { posts, items, team }, revalidate: 60 }
}) satisfies GetStaticProps

export default HomePage
